/**
 * User Menu Icons and Configuration
 * 
 * Shared icons and menu item configurations for the user menu
 * Used by both web and plugin packages
 */

import React from 'react';
import { User, CircleHelp, Share2, Code, LucideProps } from 'lucide-react';

// Note: CircleQuestionMark was renamed to CircleHelp in newer versions of lucide-react
// We use CircleHelp which is the current name for the same icon

// ============================================================================
// ICON WRAPPER COMPONENT (from old codebase)
// ============================================================================

interface IconProps extends Omit<LucideProps, 'size'> {
  icon: React.ComponentType<LucideProps>;
  size?: number | string;
  strokeWidth?: number;
  color?: string;
  fill?: string;
  className?: string;
}

const Icon: React.FC<IconProps> = ({
  icon: IconComponent,
  size = 24,
  strokeWidth = 2,
  color = 'black',
  fill,
  className = '',
  absoluteStrokeWidth = false,
  ...rest
}) => {
  return (
    <IconComponent
      size={size}
      strokeWidth={strokeWidth}
      color={color}
      {...(fill ? { fill } : {})}
      className={className}
      absoluteStrokeWidth={absoluteStrokeWidth}
      {...rest}
    />
  );
};

// ============================================================================
// MENU ITEM ICONS (from lucide-react - exact from old codebase)
// ============================================================================

export const UserIcon = () => (
  <Icon icon={User} size={23} strokeWidth={2} color="#000000" />
);

export const HelpIcon = () => (
  <Icon icon={CircleHelp} size={23} strokeWidth={2} color="#000000" />
);

export const ShareIcon = () => (
  <Icon icon={Share2} size={23} strokeWidth={2} color="#000000" />
);

export const WebsiteIcon = () => (
  <Icon icon={Code} size={23} strokeWidth={2} color="#000000" />
);

// ============================================================================
// SOCIAL MEDIA ICONS (exact from old codebase)
// ============================================================================

export const TikTokIcon = () => (
  <svg
    id="tiktok"
    width="20"
    height="20"
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M11.4801 1.11914H2.52012C1.74816 1.11914 1.12012 1.74718 1.12012 2.51914V11.4791C1.12012 12.2511 1.74816 12.8791 2.52012 12.8791H11.4801C12.2521 12.8791 12.8801 12.2511 12.8801 11.4791V2.51914C12.8801 1.74718 12.2521 1.11914 11.4801 1.11914ZM10.3618 6.24958C10.2982 6.25546 10.2338 6.25938 10.1686 6.25938C9.43416 6.25938 8.78876 5.88166 8.41328 5.31074C8.41328 6.80846 8.41328 8.51254 8.41328 8.5411C8.41328 9.85962 7.34424 10.9287 6.02572 10.9287C4.7072 10.9287 3.63816 9.85962 3.63816 8.5411C3.63816 7.22258 4.7072 6.15354 6.02572 6.15354C6.07556 6.15354 6.12428 6.15802 6.17328 6.1611V7.33766C6.12428 7.33178 6.07612 7.32282 6.02572 7.32282C5.3526 7.32282 4.80716 7.86826 4.80716 8.54138C4.80716 9.2145 5.3526 9.75994 6.02572 9.75994C6.69884 9.75994 7.29328 9.22962 7.29328 8.5565C7.29328 8.5299 7.30504 3.07018 7.30504 3.07018H8.42952C8.53536 4.07566 9.34708 4.86918 10.3618 4.94198V6.24958Z"
      fill="#101828"
    />
  </svg>
);

export const InstagramIcon = () => (
  <svg
    id="instagram"
  width="20"
    height="20"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M11.3389 13.9961L4.67225 14.0024C3.20558 14.0038 2.00458 12.8051 2.00292 11.3384L1.99658 4.67176C1.99525 3.20509 3.19392 2.00409 4.66058 2.00243L11.3272 1.99609C12.7939 1.99476 13.9949 3.19343 13.9966 4.66009L14.0029 11.3268C14.0046 12.7938 12.8056 13.9948 11.3389 13.9961Z"
      fill="url(#paint0_radial_instagram)"
    />
    <path
      d="M11.3389 13.9961L4.67225 14.0024C3.20558 14.0038 2.00458 12.8051 2.00292 11.3384L1.99658 4.67176C1.99525 3.20509 3.19392 2.00409 4.66058 2.00243L11.3272 1.99609C12.7939 1.99476 13.9949 3.19343 13.9966 4.66009L14.0029 11.3268C14.0046 12.7938 12.8056 13.9948 11.3389 13.9961Z"
      fill="url(#paint1_radial_instagram)"
    />
    <path
      d="M7.99984 10.3327C6.7135 10.3327 5.6665 9.28602 5.6665 7.99935C5.6665 6.71268 6.7135 5.66602 7.99984 5.66602C9.28617 5.66602 10.3332 6.71268 10.3332 7.99935C10.3332 9.28602 9.28617 10.3327 7.99984 10.3327ZM7.99984 6.33268C7.08084 6.33268 6.33317 7.08035 6.33317 7.99935C6.33317 8.91835 7.08084 9.66602 7.99984 9.66602C8.91884 9.66602 9.6665 8.91835 9.6665 7.99935C9.6665 7.08035 8.91884 6.33268 7.99984 6.33268Z"
      fill="white"
    />
    <path
      d="M10.5 6C10.7761 6 11 5.77614 11 5.5C11 5.22386 10.7761 5 10.5 5C10.2239 5 10 5.22386 10 5.5C10 5.77614 10.2239 6 10.5 6Z"
      fill="white"
    />
    <path
      d="M9.99984 12.3327H5.99984C4.7135 12.3327 3.6665 11.286 3.6665 9.99935V5.99935C3.6665 4.71268 4.7135 3.66602 5.99984 3.66602H9.99984C11.2862 3.66602 12.3332 4.71268 12.3332 5.99935V9.99935C12.3332 11.286 11.2862 12.3327 9.99984 12.3327ZM5.99984 4.33268C5.08084 4.33268 4.33317 5.08035 4.33317 5.99935V9.99935C4.33317 10.9183 5.08084 11.666 5.99984 11.666H9.99984C10.9188 11.666 11.6665 10.9183 11.6665 9.99935V5.99935C11.6665 5.08035 10.9188 4.33268 9.99984 4.33268H5.99984Z"
      fill="white"
    />
    <defs>
      <radialGradient
        id="paint0_radial_instagram"
        cx="0"
        cy="0"
        r="1"
        gradientUnits="userSpaceOnUse"
        gradientTransform="translate(6.45992 14.0111) scale(14.9663 14.9663)"
      >
        <stop stopColor="#FFDD55" />
        <stop offset="0.328" stopColor="#FF543F" />
        <stop offset="0.348" stopColor="#FC5245" />
        <stop offset="0.504" stopColor="#E64771" />
        <stop offset="0.643" stopColor="#D53E91" />
        <stop offset="0.761" stopColor="#CC39A4" />
        <stop offset="0.841" stopColor="#C837AB" />
      </radialGradient>
      <radialGradient
        id="paint1_radial_instagram"
        cx="0"
        cy="0"
        r="1"
        gradientUnits="userSpaceOnUse"
        gradientTransform="translate(3.92858 1.8462) scale(9.93767 6.62147)"
      >
        <stop stopColor="#4168C9" />
        <stop offset="0.999" stopColor="#4168C9" stopOpacity="0" />
      </radialGradient>
    </defs>
  </svg>
);

export const YouTubeIcon = () => (
  <svg
    id="youTube"
  width="20"
    height="20"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g clipPath="url(#clip0_youtube)">
      <path
        d="M12.677 2.33984H3.323C1.48775 2.33984 0 3.8276 0 5.66284V10.3389C0 12.1741 1.48775 13.6618 3.323 13.6618H12.677C14.5122 13.6618 16 12.1741 16 10.3389V5.66284C16 3.8276 14.5122 2.33984 12.677 2.33984ZM10.4297 8.22835L6.05451 10.315C5.93793 10.3706 5.80327 10.2856 5.80327 10.1565V5.85269C5.80327 5.7217 5.94147 5.63681 6.05829 5.69602L10.4335 7.91314C10.5636 7.97905 10.5613 8.1656 10.4297 8.22835Z"
        fill="#F61C0D"
      />
    </g>
    <defs>
      <clipPath id="clip0_youtube">
        <rect width="16" height="16" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

export const FacebookIcon = () => (
  <svg
    id="facebook"
  width="20"
    height="20"
    viewBox="0 0 12 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g clipPath="url(#clip0_facebook)">
      <path
        d="M12 6C12 2.68629 9.31371 0 6 0C2.68629 0 0 2.68629 0 6C0 8.99473 2.1941 11.477 5.0625 11.9271V7.73438H3.53906V6H5.0625V4.67813C5.0625 3.17438 5.95828 2.34375 7.32879 2.34375C7.98504 2.34375 8.67188 2.46094 8.67188 2.46094V3.9375H7.91531C7.17 3.9375 6.9375 4.40004 6.9375 4.875V6H8.60156L8.33555 7.73438H6.9375V11.9271C9.8059 11.477 12 8.99473 12 6Z"
        fill="#1877F2"
      />
      <path
        d="M8.33555 7.73438L8.60156 6H6.9375V4.875C6.9375 4.40051 7.17 3.9375 7.91531 3.9375H8.67188V2.46094C8.67188 2.46094 7.98527 2.34375 7.32879 2.34375C5.95828 2.34375 5.0625 3.17438 5.0625 4.67813V6H3.53906V7.73438H5.0625V11.9271C5.68372 12.0243 6.31628 12.0243 6.9375 11.9271V7.73438H8.33555Z"
        fill="white"
      />
    </g>
    <defs>
      <clipPath id="clip0_facebook">
        <rect width="12" height="12" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

export const TwitterIcon = () => (
  <svg
    id="twitter"
  width="20"
    height="20"
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3.08012 1.11914C1.99764 1.11914 1.12012 1.99666 1.12012 3.07914V10.9191C1.12012 12.0016 1.99764 12.8791 3.08012 12.8791H10.9201C12.0026 12.8791 12.8801 12.0016 12.8801 10.9191V3.07914C12.8801 1.99666 12.0026 1.11914 10.9201 1.11914H3.08012ZM3.66418 3.63914H5.88668L7.46496 5.88188L9.38012 3.63914H10.0801L7.78105 6.33086L10.6161 10.3591H8.3941L6.56262 7.75711L4.34012 10.3591H3.64012L6.24652 7.30813L3.66418 3.63914ZM4.73605 4.19914L8.68613 9.79914H9.54418L5.5941 4.19914H4.73605Z"
      fill="#101828"
    />
  </svg>
);

export const LinkedInIcon = () => (
  <svg
    id="linkedin"
    width="20"
    height="20"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M14 12.3333C14 13.254 13.254 14 12.3333 14H3.66667C2.74633 14 2 13.254 2 12.3333V3.66667C2 2.746 2.74633 2 3.66667 2H12.3333C13.254 2 14 2.746 14 3.66667V12.3333Z"
      fill="#0078D4"
    />
    <path
      opacity="0.05"
      d="M9.99984 12.3327V8.96635C9.99984 8.40335 9.72684 8.06702 9.26917 8.06702C8.9975 8.06702 8.79784 8.22002 8.67617 8.52168C8.6705 8.54302 8.6625 8.63002 8.66584 8.89302L8.6665 12.3327H6.33317V5.99935H8.6665V6.35302C9.00717 6.11802 9.42484 5.99935 9.9125 5.99935C11.4282 5.99935 12.3328 7.03035 12.3328 8.75735L12.3332 12.3327H9.99984ZM3.6665 12.3327V5.99935H4.81884C4.15117 5.99935 3.6665 5.50868 3.6665 4.83235C3.6665 4.15668 4.15917 3.66602 4.83784 3.66602C5.5085 3.66602 5.98617 4.14302 5.99984 4.82568C5.99984 5.50702 5.50684 5.99935 4.82817 5.99935H5.99984V12.3327H3.6665Z"
      fill="black"
    />
    <path
      opacity="0.07"
      d="M10.1668 12.1673V8.96765C10.1668 8.30998 9.82316 7.90165 9.2695 7.90165C8.83783 7.90165 8.6245 8.20565 8.52183 8.46065C8.4945 8.52698 8.49816 8.79032 8.4995 8.90265L8.50016 12.1673H6.50016V6.16732H8.50016V6.71332C8.76516 6.43898 9.19183 6.16732 9.91283 6.16732C11.3238 6.16732 12.1665 7.13598 12.1665 8.75865L12.1668 12.1673H10.1668ZM3.8335 12.1673V6.16732H5.8335V12.1673H3.8335ZM4.81916 5.83398C4.24816 5.83398 3.8335 5.41332 3.8335 4.83365C3.8335 4.25432 4.25616 3.83398 4.83816 3.83398C5.41283 3.83398 5.82183 4.24365 5.8335 4.83032C5.8335 5.41332 5.41083 5.83398 4.8285 5.83398H4.81916Z"
      fill="black"
    />
    <path
      d="M4 6.33333H5.66667V12H4V6.33333ZM4.82833 5.66667H4.819C4.32167 5.66667 4 5.296 4 4.833C4 4.36 4.33167 4 4.838 4C5.345 4 5.65733 4.36 5.66667 4.833C5.66667 5.29567 5.345 5.66667 4.82833 5.66667ZM12 12H10.3333V8.967C10.3333 8.23433 9.925 7.73433 9.26933 7.73433C8.769 7.73433 8.49833 8.07167 8.367 8.39767C8.319 8.51433 8.33333 8.837 8.33333 9V12H6.66667V6.33333H8.33333V7.20533C8.57367 6.83333 8.95 6.33333 9.91267 6.33333C11.1053 6.33333 11.9997 7.08333 11.9997 8.758L12 12Z"
      fill="white"
    />
  </svg>
);

// ============================================================================
// SOCIAL LINKS DATA
// ============================================================================

export const getSocialLinks = () => [
  {
    id: 'tiktok',
    name: 'TikTok',
    url: 'https://www.tiktok.com/@ideoz_io?lang=en',
    icon: <TikTokIcon />,
  },
  {
    id: 'instagram',
    name: 'Instagram',
    url: 'https://www.instagram.com/ideoz.ai/',
    icon: <InstagramIcon />,
  },
  {
    id: 'youtube',
    name: 'YouTube',
    url: 'https://www.youtube.com/channel/UC-fwiEhZZVfbrIwcbWjfLxA/',
    icon: <YouTubeIcon />,
  },
  {
    id: 'facebook',
    name: 'Facebook',
    url: 'https://www.facebook.com/ideozai.p',
    icon: <FacebookIcon />,
  },
  {
    id: 'twitter',
    name: 'Twitter',
    url: 'https://x.com/IdeozAi',
    icon: <TwitterIcon />,
  },
  {
    id: 'linkedin',
    name: 'LinkedIn',
    url: 'https://www.linkedin.com/company/ideozsplugin',
    icon: <LinkedInIcon />,
  },
];
